import RestEndpointsEnum from "../models/RestEndpointsEnum";
import { buildRestUrl, buildAuthHeadersForFetch } from "./RestService";
import IUserUI from "../models/IUserUI";
import ISupplierModel from "../models/ISupplierModel";
import IDeliverySchedulesResponse from "../models/DTO/IDeliverySchedulesResponse";
import IDeliveryScheduleResponse from "../models/DTO/IDeliveryScheduleResponse";
import IDeliveryScheduleDayResponse from "../models/DTO/IDeliveryScheduleDayResponse";
import IUpsertDeliveryScheduleDayDTO from "../models/DTO/IUpsertDeliveryScheduleDayDTO";
import IVendorDeliveryDeadlineSettingsResponse from "../models/DTO/IVendorDeliveryDeadlineSettingsResponse";
import IDeliveryDeadlineUsersResponse from "../models/DTO/IDeliveryDeadlineUsersResponse";
import IMessageResponse from "../models/DTO/IMessageResponse";

/**
 * Service for the Delivery Deadlines feature (supplier-user auth).
 *
 * Follows the repo's native-fetch service convention (see AccountNotesService):
 * build the URL with buildRestUrl, attach auth headers with buildAuthHeadersForFetch,
 * and throw on a non-2xx response. The thrown Error message prefers the API's
 * { error } / { message } body so callers can surface it directly in a toast.
 */

/**
 * Parses a non-ok response body and throws an Error carrying the API's
 * `error` or `message`, falling back to the HTTP status text.
 */
const throwApiError = async (response: Response): Promise<never> => {
    const body = await response.json().catch(() => ({}) as IMessageResponse);
    throw new Error(body.error || body.message || response.statusText);
};

// ----- Schedule templates -----

const getDeliverySchedules = async (userModel: IUserUI, supplierModel: ISupplierModel): Promise<IDeliverySchedulesResponse> => {
    const url = buildRestUrl({ endpoint: RestEndpointsEnum.DeliverySchedulesGet });
    const headers = buildAuthHeadersForFetch({ userModel, supplierModel });

    const response = await fetch(url, { method: "GET", headers });

    if (!response.ok) {
        await throwApiError(response);
    }

    return (await response.json()) as IDeliverySchedulesResponse;
};

const getDeliverySchedule = async (
    scheduleGuid: string,
    userModel: IUserUI,
    supplierModel: ISupplierModel
): Promise<IDeliveryScheduleResponse> => {
    const url = buildRestUrl({ endpoint: RestEndpointsEnum.DeliveryScheduleGet, params: { scheduleGuid } });
    const headers = buildAuthHeadersForFetch({ userModel, supplierModel });

    const response = await fetch(url, { method: "GET", headers });

    if (!response.ok) {
        await throwApiError(response);
    }

    return (await response.json()) as IDeliveryScheduleResponse;
};

const createDeliverySchedule = async (
    body: { name: string; active?: boolean },
    userModel: IUserUI,
    supplierModel: ISupplierModel
): Promise<IDeliveryScheduleResponse> => {
    const url = buildRestUrl({ endpoint: RestEndpointsEnum.DeliveryScheduleCreate });
    const headers = buildAuthHeadersForFetch({ userModel, supplierModel });

    const response = await fetch(url, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(body),
    });

    if (!response.ok) {
        await throwApiError(response);
    }

    return (await response.json()) as IDeliveryScheduleResponse;
};

const updateDeliverySchedule = async (
    scheduleGuid: string,
    body: { name?: string; active?: boolean },
    userModel: IUserUI,
    supplierModel: ISupplierModel
): Promise<IDeliveryScheduleResponse> => {
    const url = buildRestUrl({ endpoint: RestEndpointsEnum.DeliveryScheduleUpdate, params: { scheduleGuid } });
    const headers = buildAuthHeadersForFetch({ userModel, supplierModel });

    const response = await fetch(url, {
        method: "PUT",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(body),
    });

    if (!response.ok) {
        await throwApiError(response);
    }

    return (await response.json()) as IDeliveryScheduleResponse;
};

const deleteDeliverySchedule = async (
    scheduleGuid: string,
    userModel: IUserUI,
    supplierModel: ISupplierModel
): Promise<IMessageResponse> => {
    const url = buildRestUrl({ endpoint: RestEndpointsEnum.DeliveryScheduleDelete, params: { scheduleGuid } });
    const headers = buildAuthHeadersForFetch({ userModel, supplierModel });

    const response = await fetch(url, { method: "DELETE", headers });

    if (!response.ok) {
        await throwApiError(response);
    }

    return (await response.json()) as IMessageResponse;
};

// ----- Schedule days (upsert keyed on delivery_day_of_week) -----

const upsertDeliveryScheduleDay = async (
    scheduleGuid: string,
    body: IUpsertDeliveryScheduleDayDTO,
    userModel: IUserUI,
    supplierModel: ISupplierModel
): Promise<IDeliveryScheduleDayResponse> => {
    const url = buildRestUrl({ endpoint: RestEndpointsEnum.DeliveryScheduleDayUpsert, params: { scheduleGuid } });
    const headers = buildAuthHeadersForFetch({ userModel, supplierModel });

    const response = await fetch(url, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(body),
    });

    if (!response.ok) {
        await throwApiError(response);
    }

    return (await response.json()) as IDeliveryScheduleDayResponse;
};

const deleteDeliveryScheduleDay = async (
    scheduleGuid: string,
    dayGuid: string,
    userModel: IUserUI,
    supplierModel: ISupplierModel
): Promise<IMessageResponse> => {
    const url = buildRestUrl({ endpoint: RestEndpointsEnum.DeliveryScheduleDayDelete, params: { scheduleGuid, dayGuid } });
    const headers = buildAuthHeadersForFetch({ userModel, supplierModel });

    const response = await fetch(url, { method: "DELETE", headers });

    if (!response.ok) {
        await throwApiError(response);
    }

    return (await response.json()) as IMessageResponse;
};

// ----- Vendor reads -----

const getVendorDeadlineSettings = async (
    vendorGuid: string,
    userModel: IUserUI,
    supplierModel: ISupplierModel
): Promise<IVendorDeliveryDeadlineSettingsResponse> => {
    const url = buildRestUrl({ endpoint: RestEndpointsEnum.VendorDeliveryDeadlineSettingsGet, params: { vendorGuid } });
    const headers = buildAuthHeadersForFetch({ userModel, supplierModel });

    const response = await fetch(url, { method: "GET", headers });

    if (!response.ok) {
        await throwApiError(response);
    }

    return (await response.json()) as IVendorDeliveryDeadlineSettingsResponse;
};

const getVendorDeadlineUsers = async (
    vendorGuid: string,
    userModel: IUserUI,
    supplierModel: ISupplierModel
): Promise<IDeliveryDeadlineUsersResponse> => {
    const url = buildRestUrl({ endpoint: RestEndpointsEnum.VendorDeliveryDeadlineUsersGet, params: { vendorGuid } });
    const headers = buildAuthHeadersForFetch({ userModel, supplierModel });

    const response = await fetch(url, { method: "GET", headers });

    if (!response.ok) {
        await throwApiError(response);
    }

    return (await response.json()) as IDeliveryDeadlineUsersResponse;
};

// ----- Vendor writes -----

const assignVendorSchedule = async (
    vendorGuid: string,
    delivery_schedule_guid: string | null,
    userModel: IUserUI,
    supplierModel: ISupplierModel
): Promise<IMessageResponse> => {
    const url = buildRestUrl({ endpoint: RestEndpointsEnum.VendorDeliveryScheduleAssign, params: { vendorGuid } });
    const headers = buildAuthHeadersForFetch({ userModel, supplierModel });

    const response = await fetch(url, {
        method: "PUT",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ delivery_schedule_guid }),
    });

    if (!response.ok) {
        await throwApiError(response);
    }

    return (await response.json()) as IMessageResponse;
};

const setVendorReminders = async (
    vendorGuid: string,
    enabled: boolean,
    userModel: IUserUI,
    supplierModel: ISupplierModel
): Promise<IMessageResponse> => {
    const url = buildRestUrl({ endpoint: RestEndpointsEnum.VendorDeliveryDeadlineReminders, params: { vendorGuid } });
    const headers = buildAuthHeadersForFetch({ userModel, supplierModel });

    const response = await fetch(url, {
        method: "PUT",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ enabled }),
    });

    if (!response.ok) {
        await throwApiError(response);
    }

    return (await response.json()) as IMessageResponse;
};

const setUserOptOut = async (
    vendorGuid: string,
    userGuid: string,
    opt_out: boolean,
    userModel: IUserUI,
    supplierModel: ISupplierModel
): Promise<IMessageResponse> => {
    const url = buildRestUrl({ endpoint: RestEndpointsEnum.VendorUserDeliveryDeadlineOptOut, params: { vendorGuid, userGuid } });
    const headers = buildAuthHeadersForFetch({ userModel, supplierModel });

    const response = await fetch(url, {
        method: "PUT",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ opt_out }),
    });

    if (!response.ok) {
        await throwApiError(response);
    }

    return (await response.json()) as IMessageResponse;
};

// ----- Testing utilities -----

/**
 * Manually triggers this supplier's delivery-deadline reminder emails (hidden/dev
 * feature for testing). POST /v1/delivery-deadline-reminders/trigger.
 */
const triggerDeliveryDeadlineReminders = async (userModel: IUserUI, supplierModel: ISupplierModel): Promise<IMessageResponse> => {
    const url = buildRestUrl({ endpoint: RestEndpointsEnum.DeliveryDeadlineRemindersTrigger });
    const headers = buildAuthHeadersForFetch({ userModel, supplierModel });

    const response = await fetch(url, { method: "POST", headers });

    if (!response.ok) {
        await throwApiError(response);
    }

    return (await response.json()) as IMessageResponse;
};

const DeliveryDeadlinesService = {
    getDeliverySchedules,
    getDeliverySchedule,
    createDeliverySchedule,
    updateDeliverySchedule,
    deleteDeliverySchedule,
    upsertDeliveryScheduleDay,
    deleteDeliveryScheduleDay,
    getVendorDeadlineSettings,
    getVendorDeadlineUsers,
    assignVendorSchedule,
    setVendorReminders,
    setUserOptOut,
    triggerDeliveryDeadlineReminders,
};

export default DeliveryDeadlinesService;
